import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const MAX: Record<string, number> = { hours: 23, minutes: 59 };

/**
 * Campo de hora formado por dos inputs (horas y minutos) que se ven como uno
 * solo. Solo admite dígitos, limita al rango válido, salta de horas a minutos
 * al completar dos dígitos y rellena con cero al salir del campo.
 */
export class CoreInputTime implements Plugin {
  private hours: HTMLInputElement | null;
  private minutes: HTMLInputElement | null;
  private box: HTMLElement | null;

  constructor(private readonly element: HTMLElement) {
    this.box = element.querySelector(".core-inputTime");
    this.hours = element.querySelector('[data-time-part="hours"]');
    this.minutes = element.querySelector('[data-time-part="minutes"]');
  }

  private readonly onInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const part = input.dataset.timePart ?? "";
    let digits = input.value.replace(/\D/g, "").slice(0, 2);
    if (digits.length === 2 && Number(digits) > MAX[part]) {
      digits = String(MAX[part]);
    }
    input.value = digits;
    if (part === "hours" && digits.length === 2) this.minutes?.focus();
  };

  private readonly onKeydown = (event: KeyboardEvent): void => {
    const input = event.target as HTMLInputElement;
    if (
      input === this.minutes &&
      event.key === "Backspace" &&
      input.value === ""
    ) {
      event.preventDefault();
      this.hours?.focus();
    }
    if (input === this.hours && event.key === ":" && input.value.length > 0) {
      event.preventDefault();
      this.minutes?.focus();
    }
  };

  private readonly onBlur = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    if (input.value.length === 1) input.value = `0${input.value}`;
  };

  /** Un clic en cualquier parte de la caja (fuera de los inputs) activa las horas, el primer número. */
  private readonly onBoxClick = (event: MouseEvent): void => {
    if ((event.target as HTMLElement).closest("input")) return;
    this.hours?.focus();
  };

  private readonly onFocus = (event: Event): void => {
    (event.target as HTMLInputElement).select();
  };

  init(): void {
    this.box?.addEventListener("click", this.onBoxClick);
    for (const input of [this.hours, this.minutes]) {
      if (!input) continue;
      input.addEventListener("input", this.onInput);
      input.addEventListener("keydown", this.onKeydown);
      input.addEventListener("blur", this.onBlur);
      input.addEventListener("focus", this.onFocus);
    }
  }

  destroy(): void {
    this.box?.removeEventListener("click", this.onBoxClick);
    for (const input of [this.hours, this.minutes]) {
      if (!input) continue;
      input.removeEventListener("input", this.onInput);
      input.removeEventListener("keydown", this.onKeydown);
      input.removeEventListener("blur", this.onBlur);
      input.removeEventListener("focus", this.onFocus);
    }
  }
}

PluginRegistry.register("coreInputTime", CoreInputTime);
